import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import module from 'app/modules/well-healthiness/well-healthiness.module';
import { WellHealthinessService } from '../../services/well-healthiness.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import { finalize } from 'rxjs';
import { FuseConfirmationService } from '@fuse/services/confirmation';

@Component({
  selector: 'app-edit-well-healthiness',
  standalone: true,
  imports: module,
  templateUrl: './edit-well-healthiness.component.html',
  styleUrl: './edit-well-healthiness.component.scss'
})
export class EditWellHealthinessComponent {
  form: FormGroup;
  isLoading: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<EditWellHealthinessComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: any,
    public service: WellHealthinessService,
    private formBuilder: FormBuilder,
    private _fuseConfirmationService: FuseConfirmationService,
  ) { }

  get isReview() {
    return this.data?.status?.toLowerCase() === 'ready to review';
  }

  get isRejected() {
    return this.data?.status?.toLowerCase() === 'rejected';
  }

  get isApproved() {
    return this.data?.status?.toLowerCase() === 'approved';
  }

  ngOnInit() {
    // console.log(this.data)

    this.form = this.onForm();

    console.log(this.form.getRawValue())
    this.onWellHealthinessChange()

    if (this.isReview || this.isRejected)
      this.getOnHoldWellHealthinessRequest()
  }

  getOnHoldWellHealthinessRequest() {
    this.isLoading = true; // set it BEFORE subscribe triggers UI checks
    const params = { wellStringId: this.data?.wellStringId };

    this.service.getOnHoldWellHealthinessRequest(params)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe((res) => {
        this.form.patchValue({
          // updateBy: res.data?.updateBy,
          // updateDate: handleFormatDate(res.data?.updateDate, false),
          approvedBy: res.data?.approvedBy,
          approvedDate: handleFormatDate(res.data?.approvedDate, false),
          isWellHealthy: res.data?.wellHealthinessStatus === 'Healthy' ? true : false,
          wellHealthinessStatus: res.data?.wellHealthinessStatus,
          scoringMatrix: parseInt(res.data?.scoringMatrix),
          riskRanking: res.data?.riskRanking,
          remarks: res.data?.remarks ?? ''
        })
      })
  }

  onForm() {
    return this.formBuilder.group({
      id: new FormControl({
        value: this.data?.id,
        disabled: true,
      }),
      wellStringId: new FormControl({
        value: this.data?.wellStringId,
        disabled: true,
      }),
      stringShortName: new FormControl({
        value: this.data?.stringShortName,
        disabled: true,
      }),
      status: new FormControl({
        value: this.data?.status,
        disabled: true,
      }),
      updateBy: new FormControl({
        value: this.data?.updateBy,
        disabled: true,
      }),
      updateDate: new FormControl({
        value: handleFormatDate(this.data?.updateDate, false),
        disabled: true,
      }),
      approvedBy: new FormControl({
        value: this.isApproved ? this.data.approvedBy : '',
        disabled: true,
      }),
      approvedDate: new FormControl({
        value: this.isApproved ? handleFormatDate(this.data?.approvedDate, false) : '',
        disabled: true,
      }),
      // failEquipment: new FormControl({
      //   value: this.params.payload?.failEquipment,
      //   disabled: this.isViewOnly,
      // }, [Validators.required]),
      // failureDate: new FormControl({
      //   value: this.params.payload?.failureDate,
      //   disabled: this.isViewOnly,
      // }, [Validators.required]),
      // problemReported: new FormControl({
      //   value: this.params.payload?.problemReported,
      //   disabled: this.isViewOnly,
      // }, [Validators.required]),
      // detailDesc: new FormControl({
      //   value: this.params.payload?.detailDesc,
      //   disabled: this.isViewOnly,
      // }, [Validators.required]),
      // forwardActionPlan: new FormControl({
      //   value: this.params.payload?.forwardActionPlan,
      //   disabled: this.isViewOnly,
      // }, [Validators.required]),
      isWellHealthy: new FormControl({
        value: this.data?.wellHealthinessStatus === 'Healthy' ? true : false,
        disabled: this.isReview,
      }, [Validators.required]),
      wellHealthinessStatus: new FormControl({
        value: this.data?.wellHealthinessStatus,
        disabled: this.isReview,
      }, [Validators.required]),
      scoringMatrix: new FormControl({
        value: parseInt(this.data?.scoringMatrix), // this.data?.scoringMatrix,
        disabled: this.isReview,
      }, [Validators.required]),
      riskRanking: new FormControl({
        value: this.data?.riskRanking,
        disabled: this.isReview,
      }),
      remarks: new FormControl({
        value: this.isReview ? this.data?.remarks : this.data?.remarks ?? null,
        disabled: this.isReview,
      }, [Validators.required]),
    });
  }

  onWellHealthinessChange() {
    this.form.get('isWellHealthy')?.valueChanges.subscribe((value) =>
      this.form.get('scoringMatrix')?.setValue(null));
  }

  overrideWellHealthiness(status: string) {
    let message = 'Are you sure you want to update Well Healthiness Status?'
    if (status == 'Approved') {
      message = 'Are you sure you want to approve Well Healthiness Status to Approved'
    } else if(status == 'Rejected') {
      message = 'Are you sure you want to reject Well Healthiness Status to Rejected'
    }

    const confirmation = this._fuseConfirmationService.open({
      title: 'Update Well Healthiness Status',
      message: message,
      actions: {
        confirm: {
          color: 'primary',
          label: 'Confirm',
        },
      },
    });

    confirmation.afterClosed().subscribe((result) => {
      // If the confirm button pressed...
      if (result === 'confirmed') {
        this.isLoading = true;
        const riskRanking = this.getRiskRanking(this.form.get('scoringMatrix')?.value)

        const payload = {
          wellStringId: this.data?.wellStringId,
          wellHealthinessStatus: this.form.get('isWellHealthy')?.value ? 'Healthy' : 'Unhealthy',
          riskRanking: riskRanking,
          status: status,
          remarks: this.form.get('remarks')?.value
        }

        this.service.manualOverrideWellHealthiness(payload)
          .pipe(finalize(() => this.isLoading = false))
          .subscribe((res) => {
            this.dialogRef.close({ data: res.data, status: 'success' });
          })
      }
    })
  }

  getRiskRanking(scoringMatrix: number) {
    console.log(scoringMatrix)
    switch (scoringMatrix) {
      case 0:
        return 'Healthy no risk';
      case 1:
        return 'Healthy low risk';
      case 2:
        return 'Healthy medium risk';
      case 3:
        return 'Unhealthy high risk';
      case 4:
        return 'Unhealthy very high risk';
      default:
        return '';
    }
  }
}
